import { isNationalIdWellFormed, isPhoneWellFormed } from '@/lib/patientIdentifiers';
import type { PatientSex } from '@/types/patients';

/**
 * The demographics collected at registration, wherever that happens: the staff
 * quick-create form, the public self-registration page, and the staff review of
 * a submission. One shape so the three surfaces cannot drift.
 */
export interface PatientDetailsDraft {
  fullName: string;
  sex: PatientSex;
  /** `yyyy-mm-dd`, or null when the patient gave an age instead. */
  dob: string | null;
  /** Free text as typed; only used when `dob` is null. */
  age: string;
  phone: string;
  nationalId: string;
}

export function emptyPatientDetails(
  overrides: Partial<PatientDetailsDraft> = {},
): PatientDetailsDraft {
  return {
    fullName: '',
    sex: 'female',
    dob: null,
    age: '',
    phone: '',
    nationalId: '',
    ...overrides,
  };
}

/**
 * Per-field validation messages, as i18n keys. Null/absent means the field is
 * fine. `fullName` is supplied by the caller rather than derived here — each
 * surface words "we need a name" differently (the public page addresses the
 * patient, the staff form addresses a colleague).
 */
export interface PatientDetailsErrors {
  fullName?: string | null;
  phone?: string | null;
  nationalId?: string | null;
}

/**
 * Check the two fields the server now rejects outright, BEFORE submitting.
 *
 * This is not belt-and-braces duplication of server validation — it is load
 * bearing. Quick-create writes the patient first and applies the NIC in a
 * follow-up PATCH, so a NIC the server refuses would be dropped on the floor
 * behind a generic "some extras didn't save" warning, leaving a patient record
 * that silently lacks the number the staff member typed. Catching it here is the
 * only place the person can still fix it.
 *
 * The rules come from `@synapse/patient-identifiers`, the same module the server
 * validates with, so the two cannot disagree about what a NIC is.
 *
 * Both fields are optional: empty is valid, malformed is not.
 */
export function validatePatientDetails(draft: PatientDetailsDraft): PatientDetailsErrors {
  const errors: PatientDetailsErrors = {};

  const phone = draft.phone.trim();
  if (phone !== '' && !isPhoneWellFormed(phone)) {
    errors.phone = 'patients.quickCreate.phoneInvalid';
  }

  const nationalId = draft.nationalId.trim();
  if (nationalId !== '' && !isNationalIdWellFormed(nationalId)) {
    errors.nationalId = 'patients.quickCreate.nationalIdInvalid';
  }

  return errors;
}

/** True when nothing in `validatePatientDetails` objected. */
export function hasPatientDetailsErrors(errors: PatientDetailsErrors): boolean {
  return Boolean(errors.fullName || errors.phone || errors.nationalId);
}

const MAX_AGE = 150;

/**
 * Resolve the date of birth to persist.
 *
 * An explicit DOB always wins. Otherwise an age is turned into an approximate
 * 1-January date — patients frequently know their age but not their birthday,
 * and an approximate year is far more useful clinically than nothing. A
 * nonsensical age (blank, non-numeric, negative, absurd) yields null rather
 * than a garbage date.
 *
 * `now` is injectable so tests don't depend on the year they run in.
 */
export function resolveDateOfBirth(
  dob: string | null,
  age: string,
  now: Date = new Date(),
): string | null {
  if (dob != null) return dob;

  const trimmed = age.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > MAX_AGE) return null;

  const year = now.getUTCFullYear() - Math.floor(parsed);
  return `${String(year).padStart(4, '0')}-01-01`;
}

/** Today as `yyyy-mm-dd` (UTC) — the latest date a DOB picker may offer. */
export function todayIsoDate(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
